import EntityService from "@/services/entityService";

// Relatório de Estoque CONSOLIDADO: uma linha por item, somando todos os locais de estoque.
// Fonte: StockBalance (uma linha por item x local) + Item (unidade) + ItemUnidade (sigla).
// Somente leitura.

const stockBalanceService = new EntityService("StockBalance");
const itemService = new EntityService("Item");
const itemUnidadeService = new EntityService("ItemUnidade");

const TAMANHO_LOTE_IDS = 100;

const toNumber = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

const toText = (valor, fallback = "") => {
  if (valor === null || valor === undefined) return fallback;
  const t = String(valor).trim();
  return t || fallback;
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const extrairIdRelacao = (valor) => {
  if (!valor) return "";
  if (typeof valor === "object") return toText(valor.id || valor._id);
  return toText(valor);
};

const buscarTodosStockBalance = async () => {
  const todos = [];
  const limit = 1000;
  for (let page = 1; page <= 50; page++) {
    const response = await stockBalanceService.getAll(page, limit, "", "", null, null, null, null);
    const lista = extrairLista(response);
    todos.push(...lista);
    const pages = response?.data?.data?.pages;
    if (!lista.length || (pages && page >= pages) || lista.length < limit) break;
  }
  return todos;
};

const buscarUnidadesPorItem = async (idsItens) => {
  const unidadePorItem = new Map();
  const ids = Array.from(idsItens).filter(Boolean);
  const lotes = [];
  for (let i = 0; i < ids.length; i += TAMANHO_LOTE_IDS) lotes.push(ids.slice(i, i + TAMANHO_LOTE_IDS));

  await Promise.all(lotes.map(async (lote) => {
    try {
      const response = await itemService.getAll(1, lote.length, "", "", lote, null, null, null);
      for (const item of extrairLista(response)) {
        unidadePorItem.set(toText(item.id), extrairIdRelacao(item.unidade));
      }
    } catch (e) {
      // sem unidade para este lote; segue com "-"
    }
  }));

  const siglaPorUnidade = new Map();
  try {
    const response = await itemUnidadeService.getAll(1, 500, "", "", null, null, null, null);
    for (const u of extrairLista(response)) {
      siglaPorUnidade.set(toText(u.id), toText(u.sigla) || toText(u.descricao));
    }
  } catch (e) {
    // ignora
  }

  return { unidadePorItem, siglaPorUnidade };
};

export default {
  async listarEstoqueConsolidado() {
    try {
      const linhas = await buscarTodosStockBalance();
      const idsItens = new Set(linhas.map((l) => extrairIdRelacao(l.item)).filter(Boolean));
      const { unidadePorItem, siglaPorUnidade } = await buscarUnidadesPorItem(idsItens);

      const mapa = new Map();
      for (const l of linhas) {
        const idItem = extrairIdRelacao(l.item);
        const codigo = toText(l.itemCodigo);
        const chave = idItem || codigo;
        if (!chave) continue;

        const saldo = toNumber(l.quantidadeTotal);
        const disponivel = toNumber(l.quantidadeDisponivel);
        const reserva = toNumber(l.quantidadeReservado);
        const unitario = toNumber(l.valorRealUnitario);
        const local = toText(l.localEstoqueDescricao, "Sem local");

        let acc = mapa.get(chave);
        if (!acc) {
          const idUnidade = unidadePorItem.get(idItem) || "";
          acc = {
            _id: chave,
            codigo,
            descricao: toText(l.itemDescricao, "-"),
            um: siglaPorUnidade.get(idUnidade) || "-",
            reserva: 0,
            disponivel: 0,
            saldo: 0,
            valorTotal: 0,
            somaUnitarios: 0,
            qtdLinhas: 0,
            locais: []
          };
          mapa.set(chave, acc);
        }

        acc.reserva += reserva;
        acc.disponivel += disponivel;
        acc.saldo += saldo;
        acc.valorTotal += saldo * unitario;
        acc.somaUnitarios += unitario;
        acc.qtdLinhas += 1;
        acc.locais.push({ local, reserva, disponivel, saldo, unitario });
      }

      return Array.from(mapa.values())
        .map((a) => ({
          _id: a._id,
          codigo: a.codigo,
          descricao: a.descricao,
          um: a.um,
          reserva: a.reserva,
          disponivel: a.disponivel,
          saldo: a.saldo,
          // média ponderada pelo saldo; sem saldo, média simples dos unitários dos locais
          prUnitReal: a.saldo > 0 ? a.valorTotal / a.saldo : (a.qtdLinhas ? a.somaUnitarios / a.qtdLinhas : 0),
          valorTotal: a.valorTotal,
          locais: a.locais
        }))
        .sort((x, y) => String(x.codigo).localeCompare(String(y.codigo), "pt-BR", { numeric: true }));
    } catch (erro) {
      console.error("Erro ao listar estoque consolidado:", erro);
      return [];
    }
  }
};
